'use client';

import { useMemo, useState } from 'react';
import { Hide, Show, VStack } from '@chakra-ui/react';
import { v4 as uuid } from 'uuid';

import { useGiftcard } from 'context/giftcard';
import { resolvePath } from 'utils/helpers';
import { useRouter } from 'hooks/useRouter';
import { useNetwork } from 'context/network';
import { createSponsoredGiftcardActivation } from 'services/client/repositories/giftcard';
import { Paths } from 'utils/paths';
import { usePageTextContent } from 'context/pageTextContent';
import { useAlertDialog } from 'context/alertDialog';
import * as AppLayout from 'components/AppLayout';

import { ActivatedGiftcard } from './ActivatedGiftcard';
import { ActivateGiftcardForm } from './ActivateGiftcardForm';

export interface ActivationSponsoredGiftcardSearchParams {
  form?: 'digital';
  rt: string;
}

export const ActivationPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [activatedGiftcard, setActivatedGiftcard] =
    useState<AdaptedGiftcard | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(uuid());

  const { giftcard: _giftcard, updateGiftcard } = useGiftcard();

  const { router, query } =
    useRouter<ActivationSponsoredGiftcardSearchParams>();

  const { network } = useNetwork();

  const giftcard = activatedGiftcard || _giftcard;

  const { activation, isActivated = false } = giftcard;

  const { isActivationOpen = false } = activation.sponsor || {};

  const { content } =
    usePageTextContent<SponsoredGiftcardActivationPageContent>();

  const { activate, activationExpired, activationErrorMessage } = content;

  const { onOpenAlertDialog } = useAlertDialog();

  const subtitle = useMemo(() => {
    if (isActivated) return;

    if (!isActivationOpen) return activationExpired;

    return activate;
  }, [giftcard.activation, isActivated]);

  const onOpenGiftcard = () => {
    if (!activatedGiftcard) return;

    if (query.form === 'digital') {
      const fetchPage = resolvePath({
        pathname: Paths.GIFTCARD_FETCH_PAGE,
        query: {
          giftcardId: activatedGiftcard.id,
          t: activatedGiftcard.token,
          form: query.form,
        },
      });

      router.push(fetchPage);
      updateGiftcard(activatedGiftcard);

      return;
    }

    const networkUrl = network?.fqdn ?? network?.slugFqdn;

    router.push(`${networkUrl}/search`);
  };

  const onActivate = async () => {
    try {
      if (isActivated) return;

      setIsLoading(true);

      const _activatedGiftcard = await createSponsoredGiftcardActivation({
        giftcardId: giftcard.id,
        idempotencyKey,
        redeemToken: giftcard.redeemToken || query.rt,
      });

      if (query.form === 'digital') {
        const fetchPage = resolvePath({
          pathname: Paths.GIFTCARD_FETCH_PAGE,
          query: {
            giftcardId: _activatedGiftcard.id,
            t: _activatedGiftcard.token,
          },
        });

        router.prefetch(fetchPage);
      }

      setActivatedGiftcard(_activatedGiftcard);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setIdempotencyKey(uuid());

      onOpenAlertDialog({ content: activationErrorMessage, status: 'error' });
    }
  };

  if (activatedGiftcard) {
    return (
      <ActivatedGiftcard
        activatedGiftcard={activatedGiftcard}
        onOpenGiftcard={onOpenGiftcard}
        isDigital={query.form === 'digital'}
      />
    );
  }

  return (
    <AppLayout.Root variant={isActivationOpen ? 'default' : 'error'}>
      <AppLayout.DesktopView />

      <AppLayout.MainView>
        <AppLayout.MainContainer>
          <AppLayout.NetworkLogo />

          <AppLayout.GiftcardAmount subtitle={subtitle} />

          <AppLayout.Main>
            <ActivateGiftcardForm
              giftcard={giftcard}
              isActivationOpen={isActivationOpen}
              isLoading={isLoading}
              onActivate={onActivate}
            />

            <Show above="md" ssr>
              <VStack gap="0.5rem">
                <AppLayout.Sponsors />

                <AppLayout.GiftcardDescription giftcard={giftcard} />
              </VStack>
            </Show>
          </AppLayout.Main>

          <Hide above="md" ssr>
            <VStack
              my="1.875rem"
              gap="0.5rem"
              px="1.5rem"
              maxW="max-giftcards-pages-main-sm"
            >
              <AppLayout.Sponsors />

              <AppLayout.GiftcardDescription giftcard={giftcard} />
            </VStack>
          </Hide>
        </AppLayout.MainContainer>
      </AppLayout.MainView>
    </AppLayout.Root>
  );
};
